import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { categories } from '@/mocks/categories';
import { pillarArticles } from '@/mocks/articles';
import { productGroups } from '@/mocks/products';
import ProductCarousel from '@/components/ProductCarousel';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';

const popularResources = [
  {
    key: 'spreadsheet',
    link: '/article',
    external: false,
    image: 'https://readdy.ai/api/search-image?query=A%20tidy%20product%20spreadsheet%20with%20colorful%20category%20rows%20on%20a%20laptop%20screen%2C%20soft%20office%20lighting%2C%20organized%20data%20concept%2C%20clean%20editorial%20tech%20photography&width=400&height=300&seq=res-spreadsheet&orientation=landscape',
  },
  {
    key: 'review',
    link: 'https://kaspreadsheet.com',
    external: true,
    image: 'https://readdy.ai/api/search-image?query=Five%20glowing%20golden%20stars%20above%20a%20handwritten%20review%20card%20on%20a%20warm%20desk%2C%20customer%20feedback%20concept%2C%20soft%20warm%20lighting%2C%20minimal%20editorial%20still%20life%20photography&width=400&height=300&seq=res-review&orientation=landscape',
  },
  {
    key: 'shippingCost',
    link: 'https://kaspreadsheet.com',
    external: true,
    image: 'https://readdy.ai/api/search-image?query=A%20cardboard%20shipping%20box%20with%20packing%20tape%20next%20to%20a%20small%20calculator%20on%20a%20clean%20surface%2C%20shipping%20cost%20concept%2C%20bright%20soft%20lighting%2C%20minimal%20ecommerce%20photography&width=400&height=300&seq=res-shipping&orientation=landscape',
  },
  {
    key: 'warehouse',
    link: 'https://kaspreadsheet.com',
    external: true,
    image: 'https://readdy.ai/api/search-image?query=Neat%20rows%20of%20cardboard%20boxes%20on%20metal%20warehouse%20shelves%2C%20bright%20industrial%20lighting%2C%20organized%20logistics%20concept%2C%20clean%20editorial%20photography&width=400&height=300&seq=res-warehouse&orientation=landscape',
  },
  {
    key: 'coupon',
    link: 'https://kaspreadsheet.com',
    external: true,
    image: 'https://readdy.ai/api/search-image?query=A%20paper%20discount%20coupon%20with%20dashed%20perforated%20edges%20held%20over%20a%20shopping%20bag%2C%20savings%20concept%2C%20soft%20warm%20lighting%2C%20minimal%20retail%20still%20life%20photography&width=400&height=300&seq=res-coupon&orientation=landscape',
  },
  {
    key: 'discountCode',
    link: 'https://kaspreadsheet.com',
    external: true,
    image: 'https://readdy.ai/api/search-image?query=A%20hanging%20price%20tag%20with%20a%20percentage%20symbol%20on%20a%20soft%20pastel%20background%2C%20discount%20offer%20concept%2C%20bright%20clean%20lighting%2C%20minimal%20promotional%20photography&width=400&height=300&seq=res-discount&orientation=landscape',
  },
  {
    key: 'legit',
    link: 'https://kaspreadsheet.com',
    external: true,
    image: 'https://readdy.ai/api/search-image?query=A%20green%20verification%20checkmark%20badge%20beside%20a%20document%20on%20a%20clean%20desk%2C%20trust%20and%20legitimacy%20concept%2C%20soft%20bright%20lighting%2C%20minimal%20editorial%20photography&width=400&height=300&seq=res-legit&orientation=landscape',
  },
  {
    key: 'qc',
    link: 'https://kaspreadsheet.com',
    external: true,
    image: 'https://readdy.ai/api/search-image?query=A%20magnifying%20glass%20hovering%20over%20a%20folded%20t-shirt%20detail%2C%20quality%20inspection%20concept%2C%20crisp%20studio%20lighting%2C%20clean%20product%20photography&width=400&height=300&seq=res-qc&orientation=landscape',
  },
  {
    key: 'trustedSellers',
    link: 'https://kaspreadsheet.com',
    external: true,
    image: 'https://readdy.ai/api/search-image?query=A%20neat%20storefront%20facade%20with%20an%20open%20sign%20and%20small%20flags%2C%20trusted%20seller%20concept%2C%20warm%20daylight%2C%20clean%20minimal%20editorial%20photography&width=400&height=300&seq=res-sellers&orientation=landscape',
  },
  {
    key: 'buyingGuides',
    link: 'https://kaspreadsheet.com',
    external: true,
    image: 'https://readdy.ai/api/search-image?query=An%20open%20guidebook%20with%20bookmarks%20and%20a%20small%20pen%20on%20a%20bright%20desk%2C%20learning%20and%20guidance%20concept%2C%20soft%20natural%20light%2C%20minimal%20editorial%20still%20life%20photography&width=400&height=300&seq=res-guides&orientation=landscape',
  },
];

export default function Home() {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />

      {/* Hero Section */}
      <section className="relative w-full overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=A%20vibrant%20editorial%20flatlay%20of%20colorful%20sneakers%2C%20folded%20streetwear%2C%20caps%2C%20bags%20and%20accessories%20arranged%20in%20a%20flowing%20diagonal%20composition%20on%20a%20clean%20off-white%20background%2C%20soft%20bright%20studio%20lighting%2C%20premium%20curated%20fashion%20collection%2C%20high%20detail%20product%20photography&width=1600&height=900&seq=hero-main-v2&orientation=landscape"
            alt="AllChinaBuy Spreadsheet product collection"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50" />
        </div>
        <div className="relative w-full px-4 md:px-6 lg:px-10 py-24 md:py-32 lg:py-40 flex items-center justify-center">
          <div className="text-center max-w-4xl mx-auto">
            <p className="text-xs md:text-sm font-semibold tracking-widest uppercase text-background-100/80 mb-4">
              {t('hero.eyebrow')}
            </p>
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 leading-tight mb-6">
              {t('hero.headline')}
            </h1>
            <p className="text-base md:text-lg text-background-100/90 max-w-2xl mx-auto mb-8 leading-relaxed">
              {t('hero.subheadline')}
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="px-6 py-3 md:px-8 md:py-3.5 text-sm md:text-base font-semibold bg-primary-500 text-background-50 rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                {t('hero.ctaExplore')}
              </a>
              <Link
                to="/article"
                className="px-6 py-3 md:px-8 md:py-3.5 text-sm md:text-base font-semibold border-2 border-background-50/70 text-background-50 rounded-lg hover:bg-background-50/10 transition-colors whitespace-nowrap"
              >
                {t('hero.ctaBrowse')}
              </Link>
            </div>
            <p className="mt-6 text-xs text-background-100/70 tracking-wide">
              {t('hero.trustLine')}
            </p>
          </div>
        </div>
      </section>

      {/* What Is Section */}
      <section className="w-full px-4 md:px-6 lg:px-10 py-16 md:py-24">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-6">
            {t('whatIs.h2')}
          </h2>
          <p className="text-base md:text-lg text-foreground-600 leading-relaxed">
            {t('whatIs.copy')}
          </p>
        </div>
      </section>

      {/* Everything You Need Cards */}
      <section className="w-full px-4 md:px-6 lg:px-10 py-16 md:py-24 bg-background-100">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 text-center mb-12">
            {t('everythingNeed.h2')}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: 'ri-shopping-bag-3-line', title: t('everythingNeed.cards.productDiscovery'), desc: t('everythingNeed.cards.productDiscoveryDesc') },
              { icon: 'ri-user-search-line', title: t('everythingNeed.cards.sellerResearch'), desc: t('everythingNeed.cards.sellerResearchDesc') },
              { icon: 'ri-shield-check-line', title: t('everythingNeed.cards.qcInfo'), desc: t('everythingNeed.cards.qcInfoDesc') },
              { icon: 'ri-truck-line', title: t('everythingNeed.cards.shippingGuides'), desc: t('everythingNeed.cards.shippingGuidesDesc') },
            ].map((card, i) => (
              <div key={i} className="bg-background-50 rounded-lg p-6 md:p-8 border border-background-200 hover:border-primary-300 transition-colors">
                <div className="w-12 h-12 flex items-center justify-center bg-primary-100 rounded-lg mb-4">
                  <i className={`${card.icon} text-xl text-primary-500`} />
                </div>
                <h3 className="font-heading font-semibold text-lg text-foreground-950 mb-2">{card.title}</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">{card.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Popular Resources */}
      <section className="w-full px-4 md:px-6 lg:px-10 py-16 md:py-24">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 text-center mb-12">
            {t('popularResources.h2')}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {popularResources.map((resource) => {
              const content = (
                <div className="group flex flex-col bg-background-50 rounded-lg overflow-hidden border border-background-200 hover:border-primary-300 transition-colors">
                  <div className="aspect-[4/3] overflow-hidden">
                    <img
                      src={resource.image}
                      alt={t(`popularResources.${resource.key}`)}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-3 flex items-center justify-center">
                    <p className="text-sm font-medium text-foreground-800 group-hover:text-primary-500 transition-colors text-center leading-snug">
                      {t(`popularResources.${resource.key}`)}
                    </p>
                  </div>
                </div>
              );

              return resource.external ? (
                <a key={resource.key} href={resource.link} target="_blank" rel="noopener nofollow">
                  {content}
                </a>
              ) : (
                <Link key={resource.key} to={resource.link}>
                  {content}
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* Product Categories */}
      <section className="w-full px-4 md:px-6 lg:px-10 py-16 md:py-24 bg-background-100">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 text-center mb-12">
            {t('productCategories.h2')}
          </h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-3 md:gap-4">
            {categories.map((cat) => (
              <a
                key={cat.nameKey}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                className="group block bg-background-50 rounded-lg overflow-hidden border border-background-200 hover:border-primary-300 transition-all"
              >
                <div className="aspect-square overflow-hidden">
                  <img
                    src={cat.imageUrl}
                    alt={cat.name}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-3 text-center">
                  <p className="text-sm font-medium text-foreground-800 group-hover:text-primary-500 transition-colors">
                    {t(`categories.${cat.nameKey}`)}
                  </p>
                </div>
              </a>
            ))}
          </div>
        </div>
      </section>

      {/* Featured Products */}
      <div className="w-full py-12 md:py-16 bg-background-100">
        <div className="max-w-6xl mx-auto px-4 md:px-6 lg:px-10 mb-8">
          <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 text-center">
            Featured Products
          </h2>
          <p className="text-center text-foreground-600 max-w-2xl mx-auto mt-3 text-sm md:text-base">
            Browse curated products across every category. Click any item to view details and purchase on the main spreadsheet.
          </p>
        </div>
        {productGroups.map((group, i) => (
          <div key={group.category} className={i % 2 === 0 ? '' : 'bg-background-50/50'}>
            <ProductCarousel
              categoryName={group.category}
              categoryUrl={group.categoryUrl}
              products={group.products}
            />
          </div>
        ))}
      </div>

      {/* Pillar Articles */}
      <section className="w-full px-4 md:px-6 lg:px-10 py-16 md:py-24">
        <div className="max-w-6xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 text-center mb-4">
            {t('pillarArticles.h2')}
          </h2>
          <p className="text-center text-foreground-600 max-w-2xl mx-auto mb-12">
            Explore our in-depth guides covering everything from getting started to seller research, QC photos, and safe buying practices.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {pillarArticles.map((article) => (
              <Link
                key={article.slug}
                to={`/article/${article.slug}`}
                className="group flex flex-col bg-background-50 rounded-lg overflow-hidden border border-background-200 hover:border-primary-300 transition-colors"
              >
                <div className="aspect-[8/5] overflow-hidden">
                  <img
                    src={article.imageUrl}
                    alt={article.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                </div>
                <div className="p-5 flex flex-col flex-1">
                  <span className="inline-block px-2.5 py-1 bg-primary-100 text-primary-700 text-xs font-medium rounded-full mb-3 w-fit">
                    {article.category}
                  </span>
                  <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-950 mb-2 group-hover:text-primary-500 transition-colors leading-snug">
                    {article.title}
                  </h3>
                  <p className="text-sm text-foreground-600 leading-relaxed mb-4 line-clamp-3">
                    {article.excerpt}
                  </p>
                  <span className="mt-auto inline-flex items-center gap-2 text-sm font-semibold text-primary-500 group-hover:text-primary-600 transition-colors">
                    {t('article.readMore')}
                    <i className="ri-arrow-right-line" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* How to Use */}
      <section className="w-full px-4 md:px-6 lg:px-10 py-16 md:py-24 bg-background-100">
        <div className="max-w-4xl mx-auto">
          <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 text-center mb-12">
            {t('howToUse.h2')}
          </h2>
          <div className="space-y-4">
            {[
              t('howToUse.steps.step1'),
              t('howToUse.steps.step2'),
              t('howToUse.steps.step3'),
              t('howToUse.steps.step4'),
              t('howToUse.steps.step5'),
              t('howToUse.steps.step6'),
            ].map((step, i) => (
              <div key={i} className="flex items-start gap-4 bg-background-50 rounded-lg p-4 md:p-5 border border-background-200">
                <div className="w-8 h-8 shrink-0 flex items-center justify-center bg-primary-500 text-background-50 rounded-full text-sm font-bold">
                  {i + 1}
                </div>
                <p className="text-sm md:text-base text-foreground-700 pt-1">{step}</p>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold bg-primary-500 text-background-50 rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              {t('howToUse.cta')}
              <i className="ri-arrow-right-line" />
            </a>
          </div>
        </div>
      </section>

      {/* Research First */}
      <section className="relative w-full overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=A%20person%20thoughtfully%20reviewing%20product%20details%20on%20a%20laptop%20with%20printed%20comparison%20notes%20and%20a%20smartphone%20nearby%2C%20warm%20evening%20lamp%20lighting%2C%20focused%20research%20atmosphere%2C%20cozy%20editorial%20lifestyle%20photography%2C%20soft%20depth%20of%20field&width=1600&height=700&seq=research-bg-v2&orientation=landscape"
            alt="Research before buying"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/60 via-black/50 to-black/40" />
        </div>
        <div className="relative w-full px-4 md:px-6 lg:px-10 py-16 md:py-24">
          <div className="max-w-2xl">
            <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-background-50 mb-6">
              {t('researchFirst.h2')}
            </h2>
            <p className="text-base text-background-100/90 leading-relaxed">
              {t('researchFirst.copy')}
            </p>
          </div>
        </div>
      </section>

      {/* Stay Informed */}
      <section className="w-full px-4 md:px-6 lg:px-10 py-16 md:py-24 bg-accent-50">
        <div className="max-w-3xl mx-auto text-center">
          <div className="w-14 h-14 mx-auto flex items-center justify-center bg-accent-100 rounded-full mb-6">
            <i className="ri-information-line text-2xl text-accent-500" />
          </div>
          <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-6">
            {t('stayInformed.h2')}
          </h2>
          <p className="text-base md:text-lg text-foreground-600 leading-relaxed mb-8">
            {t('stayInformed.copy')}
          </p>
          <Link
            to="/article"
            className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold bg-accent-500 text-background-50 rounded-lg hover:bg-accent-600 transition-colors whitespace-nowrap"
          >
            {t('stayInformed.cta')}
            <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </section>

      {/* Final CTA */}
      <section className="w-full px-4 md:px-6 lg:px-10 py-16 md:py-24 bg-primary-50">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-950 mb-6">
            {t('readyToExplore.h2')}
          </h2>
          <p className="text-base md:text-lg text-foreground-600 leading-relaxed mb-8">
            {t('readyToExplore.copy')}
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-6 py-3 md:px-8 md:py-3.5 text-sm md:text-base font-semibold bg-primary-500 text-background-50 rounded-lg hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              {t('readyToExplore.ctaExplore')}
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-6 py-3 md:px-8 md:py-3.5 text-sm md:text-base font-semibold border-2 border-primary-500 text-primary-500 rounded-lg hover:bg-primary-50 transition-colors whitespace-nowrap"
            >
              {t('readyToExplore.ctaVisit')}
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}